/**
 * WebSocket Hook for Real-Time Updates
 * Phase 11: Real-Time Architecture
 * 
 * WHY: The Operational Cyber Lead mandated real-time scanner status.
 * This hook manages the WebSocket lifecycle with:
 * 1. Automatic reconnection with exponential backoff
 * 2. Connection state management
 * 3. Message handling with type discrimination
 * 
 * @see .docs/architecture/phase10_deployment_assessment.md - Expert 3 Directive
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react'

// =============================================================================
// Types
// =============================================================================

export type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting'

export interface WebSocketMessage {
  type: string
  data?: unknown
  timestamp?: string
  client_id?: string
}

export interface UseWebSocketOptions {
  /** WebSocket URL (relative path will use current host) */
  url: string
  /** Whether to connect automatically on mount */
  autoConnect?: boolean
  /** Reconnect on disconnect */
  reconnect?: boolean
  /** Maximum reconnect attempts (0 = infinite) */
  maxReconnectAttempts?: number
  /** Base delay between reconnect attempts (ms) */
  reconnectDelay?: number
  /** Maximum delay between reconnect attempts (ms) */
  maxReconnectDelay?: number
  /** 
   * JWT token for authentication (Phase 12 - TD-037)
   * WHY: The SSE mandated that WebSocket connections MUST be authenticated.
   * Pass the token from your auth store here.
   */
  token?: string
  /** Function to get token dynamically (called on each connect) */
  getToken?: () => string | null | undefined
  /** Callback when connection opens */
  onOpen?: (event: Event) => void
  /** Callback when connection closes */
  onClose?: (event: CloseEvent) => void
  /** Callback when error occurs */
  onError?: (event: Event) => void
  /** Callback when message received */
  onMessage?: (message: WebSocketMessage) => void
}

export interface UseWebSocketReturn {
  /** Current connection status */
  status: WebSocketStatus
  /** Whether currently connected */
  isConnected: boolean
  /** Last received message */
  lastMessage: WebSocketMessage | null
  /** Send a message through the socket */
  sendMessage: (message: WebSocketMessage) => void
  /** Manually connect */
  connect: () => void
  /** Manually disconnect */
  disconnect: () => void
  /** Reconnect attempts count */
  reconnectCount: number
}

// =============================================================================
// Constants
// =============================================================================

// Note: token and getToken are optional auth fields, not included in defaults
const DEFAULT_OPTIONS: Required<Omit<UseWebSocketOptions, 'url' | 'onOpen' | 'onClose' | 'onError' | 'onMessage' | 'token' | 'getToken'>> = {
  autoConnect: true,
  reconnect: true,
  maxReconnectAttempts: 10,
  reconnectDelay: 1000,
  maxReconnectDelay: 30000,
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useWebSocket(options: UseWebSocketOptions): UseWebSocketReturn {
  const {
    url,
    autoConnect = DEFAULT_OPTIONS.autoConnect,
    reconnect = DEFAULT_OPTIONS.reconnect,
    maxReconnectAttempts = DEFAULT_OPTIONS.maxReconnectAttempts,
    reconnectDelay = DEFAULT_OPTIONS.reconnectDelay,
    maxReconnectDelay = DEFAULT_OPTIONS.maxReconnectDelay,
    token,
    getToken,
    onOpen,
    onClose,
    onError,
    onMessage,
  } = options

  // State
  const [status, setStatus] = useState<WebSocketStatus>('disconnected')
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null)
  const [reconnectCount, setReconnectCount] = useState(0)

  // Refs for mutable state
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isIntentionalClose = useRef(false)

  /**
   * Build the full WebSocket URL with authentication token.
   * 
   * WHY: Supports both relative paths (for same-origin) and absolute URLs.
   * In DoD environments, we often need to handle various proxy configurations.
   * 
   * WHY (Phase 12 - TD-037): Token is passed via query param for auth.
   * This is the standard pattern for WebSocket auth since headers aren't
   * supported in the browser WebSocket API.
   */
  const wsUrl = useMemo(() => {
    let baseUrl: string
    
    if (url.startsWith('ws://') || url.startsWith('wss://')) {
      baseUrl = url
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const path = url.startsWith('/') ? url : `/${url}`
      baseUrl = `${protocol}//${host}${path}`
    }
    
    // Phase 12: Append token to URL if provided
    const authToken = getToken?.() ?? token
    if (authToken) {
      const separator = baseUrl.includes('?') ? '&' : '?'
      return `${baseUrl}${separator}token=${encodeURIComponent(authToken)}`
    }
    
    return baseUrl
  }, [url, token, getToken])

  /**
   * Calculate reconnect delay with exponential backoff.
   * 
   * WHY: DoD networks are flaky. Exponential backoff prevents
   * hammering the server during network issues.
   */
  const getReconnectDelay = useCallback(
    (attempt: number): number => {
      const delay = reconnectDelay * Math.pow(2, attempt)
      return Math.min(delay, maxReconnectDelay)
    },
    [reconnectDelay, maxReconnectDelay]
  )

  /**
   * Clean up reconnect timeout.
   */
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  /**
   * Establish WebSocket connection.
   */
  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }

    clearReconnectTimeout()
    isIntentionalClose.current = false
    setStatus('connecting')

    try {
      const ws = new WebSocket(wsUrl)

      ws.onopen = (event) => {
        setStatus('connected')
        setReconnectCount(0)
        onOpen?.(event)
      }

      ws.onclose = (event) => {
        wsRef.current = null
        
        if (isIntentionalClose.current) {
          setStatus('disconnected')
          onClose?.(event)
          return
        }

        // Attempt reconnection
        if (reconnect && (maxReconnectAttempts === 0 || reconnectCount < maxReconnectAttempts)) {
          setStatus('reconnecting')
          const delay = getReconnectDelay(reconnectCount)
          
          console.debug(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectCount + 1})`)
          
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectCount((prev) => prev + 1)
            connect()
          }, delay)
        } else {
          setStatus('disconnected')
        }
        
        onClose?.(event)
      }

      ws.onerror = (event) => {
        console.error('[WebSocket] Error:', event)
        onError?.(event)
      }

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          setLastMessage(message)
          onMessage?.(message)
        } catch (error) {
          console.warn('[WebSocket] Failed to parse message:', event.data, error)
        }
      }

      wsRef.current = ws
    } catch (error) {
      console.error('[WebSocket] Failed to create connection:', error)
      setStatus('disconnected')
    }
  }, [
    wsUrl,
    reconnect,
    maxReconnectAttempts,
    reconnectCount,
    getReconnectDelay,
    clearReconnectTimeout,
    onOpen,
    onClose,
    onError,
    onMessage,
  ])

  /**
   * Close WebSocket connection.
   */
  const disconnect = useCallback(() => {
    isIntentionalClose.current = true
    clearReconnectTimeout()
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnecting')
      wsRef.current = null
    }
    
    setStatus('disconnected')
    setReconnectCount(0)
  }, [clearReconnectTimeout])

  /**
   * Send a message through the WebSocket.
   */
  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Cannot send message, socket not open')
      return
    }

    try {
      wsRef.current.send(JSON.stringify(message))
    } catch (error) {
      console.error('[WebSocket] Failed to send message:', error)
    }
  }, [])

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect()
    }

    // Cleanup on unmount
    return () => {
      disconnect()
    }
  }, [])
  // Note: We intentionally only run this on mount/unmount

  // Handle visibility changes (reconnect when tab becomes visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && status === 'disconnected' && reconnect) {
        console.debug('[WebSocket] Tab visible, attempting reconnect')
        connect()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [status, reconnect, connect])

  return {
    status,
    isConnected: status === 'connected',
    lastMessage,
    sendMessage,
    connect,
    disconnect,
    reconnectCount,
  }
}

// =============================================================================
// Specialized Hooks
// =============================================================================

/**
 * Hook for scanner status WebSocket updates.
 * 
 * WHY: Provides a typed interface specifically for scanner updates,
 * reducing boilerplate in components.
 */
export function useScannerWebSocket(
  onScannerUpdate?: (scanner: { scanner_id: string; status: string; [key: string]: unknown }) => void
) {
  const handleMessage = useCallback(
    (message: WebSocketMessage) => {
      if (message.type === 'scanner_update' && message.data) {
        onScannerUpdate?.(message.data as { scanner_id: string; status: string })
      }
    },
    [onScannerUpdate]
  )

  return useWebSocket({
    url: '/api/v1/ws/scanners',
    onMessage: handleMessage,
  })
}
